/**
 * ProfileCardContext — small cache + in-flight dedupe for profile cards (Phase A).
 *
 * Any surface opening the same card shares one fetch, so a re-open is warm
 * (helps the p50 ≤ 300 ms target). Invalidate on own-profile edit and on
 * wardrobe active-character change so the card never shows stale identity.
 */

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import { getProfileCard } from '../services/api/profileService';
import type { ProfileCard } from '../types/social';
import { useUser } from './UserContext';
import { useCharacter } from './CharacterContext';

/**
 * Backstop TTL. Explicit invalidation (own-profile edit, active-character
 * change) keeps the card fresh immediately; the TTL only bounds staleness for
 * *foreign* cards edited elsewhere so they refresh on next render per AC-03.1.
 */
const CACHE_TTL_MS = 60_000;

interface CacheEntry {
  card: ProfileCard;
  fetchedAt: number;
}

interface ProfileCardContextValue {
  /** Cached-or-fetched card. Dedupes concurrent fetches for the same id. */
  getCard: (profileId: string) => Promise<ProfileCard | null>;
  /** Synchronous peek at the cache (undefined = not loaded). */
  peek: (profileId: string) => ProfileCard | undefined;
  /** Drop a single card (or all) from the cache. */
  invalidate: (profileId?: string) => void;
}

const ProfileCardContext = createContext<ProfileCardContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function ProfileCardProvider({ children }: ProviderProps) {
  const { user } = useUser();
  const { activeCharacter } = useCharacter();
  const [cache, setCache] = useState<Record<string, CacheEntry>>({});
  const inFlight = useRef<Map<string, Promise<ProfileCard | null>>>(new Map());

  const getCard = useCallback(
    (profileId: string): Promise<ProfileCard | null> => {
      const cached = cache[profileId];
      // Serve from cache only while fresh — a stale entry falls through to a
      // refetch so an edit made elsewhere is reflected on next render.
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS)
        return Promise.resolve(cached.card);

      const existing = inFlight.current.get(profileId);
      if (existing) return existing;

      const p = getProfileCard(profileId)
        .then((card) => {
          if (card) setCache((c) => ({ ...c, [profileId]: { card, fetchedAt: Date.now() } }));
          return card;
        })
        .finally(() => {
          inFlight.current.delete(profileId);
        });
      inFlight.current.set(profileId, p);
      return p;
    },
    [cache]
  );

  const peek = useCallback((profileId: string) => cache[profileId]?.card, [cache]);

  const invalidate = useCallback((profileId?: string) => {
    if (!profileId) {
      setCache({});
      inFlight.current.clear();
      return;
    }
    setCache((c) => {
      const next = { ...c };
      delete next[profileId];
      return next;
    });
    inFlight.current.delete(profileId);
  }, []);

  // Wardrobe active-character change → the owner's card now shows a stale
  // "Playing as" (AC-05.2). CharacterContext is the source of truth for the
  // active character (WardrobeContext derives from it), and this provider is
  // nested inside CharacterProvider, so we invalidate the owner's own card
  // (keyed by user.id, per Sidebar's self ProfileCardTrigger) on each change.
  const activeCharId = activeCharacter?.id;
  const prevCharId = useRef<string | undefined>(activeCharId);
  useEffect(() => {
    if (prevCharId.current === activeCharId) return;
    prevCharId.current = activeCharId;
    if (user?.id) invalidate(user.id);
  }, [activeCharId, user?.id, invalidate]);

  const value = useMemo<ProfileCardContextValue>(
    () => ({ getCard, peek, invalidate }),
    [getCard, peek, invalidate]
  );

  return <ProfileCardContext.Provider value={value}>{children}</ProfileCardContext.Provider>;
}

export function useProfileCards(): ProfileCardContextValue {
  const ctx = useContext(ProfileCardContext);
  if (!ctx) throw new Error('useProfileCards must be used within a ProfileCardProvider');
  return ctx;
}
